import React from "react";

// Helper function to get color based on peak score (matching onboarding color guide)
const getPeakScoreColor = (score: number): string => {
  // Gradient: red (0-29) -> orange (30-59) -> green (60-80) -> blue (80-100) -> gold (>100)
  if (score > 100) {
    return "#D4AF37"; // Gold
  } else if (score >= 80) {
    return "#0096c7"; // Blue (PEAK primary)
  } else if (score >= 60) {
    return "#22c55e"; // Green
  } else if (score >= 30) {
    return "#f97316"; // Orange
  } else {
    return "#ef4444"; // Red
  }
};

interface PeakRecoveryProgressProps {
  peakScore: number;
  recoveryPoints: number;
  negativeInfluencePoints?: number;
  size?: string;
  thickness?: string;
  textSize?: string;
  onClick?: () => void;
  onAddRecovery?: () => void;
  disabled?: boolean;
}

const PeakRecoveryProgress: React.FC<PeakRecoveryProgressProps> = ({
  peakScore,
  recoveryPoints,
  negativeInfluencePoints = 0, 
  size = "7rem",
  thickness = "6px",
  textSize = "text-5xl",
  onClick,
  onAddRecovery,
  disabled = false,
}) => {
  const baseScore = 110;

const net = recoveryPoints - negativeInfluencePoints;
const displayRecovery = Math.max(net, 0);
const lostFromPeak = Math.max(-net, 0);
const solidPeakScore = Math.max(peakScore - lostFromPeak, 0);

const peakPercentage = Math.min((solidPeakScore / baseScore) * 100, 100);
const lostPercentage = Math.min((lostFromPeak / baseScore) * 100, 100);
const recoveryPercentage = Math.min((displayRecovery / baseScore) * 100, 100);

const totalScore = solidPeakScore + displayRecovery;
const displayScore = totalScore === 0 ? "^" : Math.round(totalScore);

  // Colors - use dynamic color based on peak score
  const peakColor = disabled ? "#d1d5db" : getPeakScoreColor(peakScore);
  const recoveryColor = disabled ? "#d1d5db" : "#3b82f6"; // blue-500 — visible in both light and dark

  // SVG properties
  const radius = 45;
  const circumference = 2 * Math.PI * radius;

  // Calculate stroke dash arrays for the two sections
  const peakStrokeDasharray = `${
    (peakPercentage / 100) * circumference
  } ${circumference}`;
  const lostStrokeDasharray = `${
  (lostPercentage / 100) * circumference
} ${circumference}`;
  const recoveryStrokeDasharray = `${
    (recoveryPercentage / 100) * circumference
  } ${circumference}`;

  // Calculate rotation for recovery arc to start where peak ends
  const recoveryRotation = ((peakPercentage + lostPercentage) / 100) * 360;

  return (
    <div
      className="relative"
      style={{
        width: size,
        height: size,
        // marginRight: onAddRecovery && !disabled ? '8px' : '0' // Offset for visual balance
      }}
    >
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full h-full focus:outline-none transition-opacity ${
          disabled ? "cursor-not-allowed opacity-50" : "hover:opacity-80"
        }`}
      >
        <svg
          className="transform -rotate-90 "
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
        >
          {/* Inner fill */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="oklch(var(--b1) / 0.4)"
            stroke="none"
          />

          {/* Peak score arc */}
          {peakScore > 0 && (
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={peakColor}
              strokeWidth={thickness}
              strokeDasharray={peakStrokeDasharray}
              strokeLinecap="round"
              className="transition-all duration-300 ease-in-out"
            />
          )}
          {lostFromPeak > 0 && (
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={peakColor}
              strokeOpacity={0.25}
              strokeWidth={thickness}
              strokeDasharray={lostStrokeDasharray}
              strokeLinecap="round"
              className="transition-all duration-300 ease-in-out"
              style={{
                transform: `rotate(${(peakPercentage / 100) * 360}deg)`,
                transformOrigin: "50% 50%",
              }}
            />
          )}

          {/* Recovery points arc */}
          {displayRecovery > 0 &&(
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={recoveryColor}
              strokeWidth={thickness}
              strokeDasharray={recoveryStrokeDasharray}
              strokeLinecap="round"
              className="transition-all duration-300 ease-in-out"
              style={{
                transform: `rotate(${recoveryRotation}deg)`,
                transformOrigin: "50% 50%",
              }}
            />
          )}
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={`font-bold ${textSize} ${
              disabled ? "text-base-content/50" : "text-base-content"
            }`}
          >
            {displayScore}
          </span>
        </div>
      </button>

      {/* Add Recovery Button */}
      {onAddRecovery && !disabled && peakScore > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddRecovery();
          }}
          className="absolute -top-3 -right-3 w-8 h-8 text-white rounded-full flex items-center justify-center transition-colors shadow-md border-none hover:shadow-lg"
          style={{ backgroundColor: recoveryColor }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#2563eb")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = recoveryColor)
          }
          title="Add recovery log"
        >
          <span className="text-sm font-bold leading-none">+</span>
        </button>
      )}
    </div>
  );
};

export default PeakRecoveryProgress;
